import {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {
  PageRecordType,
  PageState,
} from '@packages/systems/core/records/PageRecord';
import {ComponentCanvasState} from '@packages/systems/component-canvas/types';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {Component, Plugins} from '@packages/systems/wfdl';
import {getLiveComponentMap} from '@packages/utilities/platform-selectors';
import {getTargetElementId} from '@packages/utilities/platform';
import {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {isEditingComponentInContext} from '../utils/isEditingComponentInContext';
import {
  getActiveFrameElement,
  getVariantAssociatedWithFrame,
  getVariantRootElementFromFrame,
} from '../utils/frameUtils';
import {
  getScenarioFrameIdPreservingVariant,
  getScenarioIdForGridFrame,
} from '../utils/syncComponentPageFrames';
import {createSelector} from 'reselect';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  selectCurrentComponentCanvasPage,
  getComponentPageComponentId,
  selectIsCodeComponentCanvas,
} from '@packages/systems/component-canvas/universal';
import {
  getBaseVariantIdForCodeComponent,
  getBaseVariantIdForNativeComponent,
  type GetBaseVariantId,
} from '../utils/frameUtils';

export type ComponentCanvasStateSlice = {
  DesignerStore: DesignerStateType;
  ComponentCanvasStore: ComponentCanvasState;
  PageStore: PageState;
  UiNodeStore: UiNodeStateType;
};

/**
 * Returns the id of the component currently set in the component canvas
 */
export const selectComponentCanvasComponentId = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => {
  const page = selectCurrentComponentCanvasPage(state);
  return page && getComponentPageComponentId(page);
};

const getComponentCanvasComponent = (
  designerStore: DesignerStateType,
  page: PageRecordType
) => {
  const componentId = getComponentPageComponentId(page);
  return componentId
    ? getLiveComponentMap(designerStore).get(
        qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
      )
    : undefined;
};

/**
 * Returns the component currently set in the Component Canvas
 */
export const selectComponentCanvasComponent = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => {
  const page = selectCurrentComponentCanvasPage(state);
  return page && getComponentCanvasComponent(state.DesignerStore, page);
};

/**
 * Returns the source of the code component currently set in the component canvas
 */
export const selectComponentCanvasCodeComponentSource = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => {
  const component = selectComponentCanvasComponent(state);
  return component ? Component.getSource(component) : undefined;
};

/**
 * Returns `true` if the Component Canvas is active
 */
export const selectIsComponentCanvasActive = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => Boolean(selectCurrentComponentCanvasPage(state));

/**
 * Returns the pending canvas component ID if one exists (ie the component ID if
 * we are in the process of opening the component canvas; null otherwise)
 */
export const selectPendingCanvasComponentId = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.pendingCanvasComponentId;

/**
 * Returns whether the pending component was created as a blank component.
 */
export const selectPendingComponentEntryKind = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.pendingComponentEntryKind;

/**
 * Returns whether the pending component is a code component, which decides
 * between a hugging ('fit') and a breakpoint-wide ('full') initial frame.
 */
export const selectPendingComponentIsCodeComponent = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.pendingComponentIsCodeComponent;

/**
 * Whether entry still owes this component a Fit frame because it was created as
 * a code component and its page does not exist yet (EXT-2005). Outlives a
 * cancelled or failed first entry, unlike the pending flag above.
 */
export const selectCodeComponentFitIntents = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.codeComponentFitIntents;

/**
 * Cached frame widths for components. We keep these around so we have the right
 * width even if the API call failed and we lost the original snapshot.
 */
export const selectCachedFrameWidths = (state: ComponentCanvasStateSlice) =>
  state.ComponentCanvasStore.cachedFrameWidths;

/**
 * Returns the variants of component associated with the current component page.
 * We use this to retrieve the variants before the component is loaded and selected.
 */
export const selectComponentPageStyleVariants = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => {
  const component = selectComponentCanvasComponent(state);
  if (!component) return null;

  const variantFields = Component.getVariants(component);
  if (variantFields == null) return null;

  const variantFieldId = Object.keys(variantFields)[0];
  if (!variantFieldId) return null;

  const variants = variantFields[variantFieldId];
  return variants ?? null;
};

/**
 * Selects the `placeholderBaseVariantName` value of the component associated
 * with the active component page.
 */
export const selectComponentPagePlaceholderBaseVariantName = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}): string | null => {
  const component = selectComponentCanvasComponent(state);
  if (!component) return null;

  return Component.getPlaceholderBaseVariantName(component) ?? null;
};

/**
 * Returns the variant field id associated with the current component page.
 */
export const selectComponentVariantFieldId = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => {
  const component = selectComponentCanvasComponent(state);
  if (!component) return null;

  const variantFields = Component.getVariants(component);
  if (!variantFields) return null;

  return Object.keys(variantFields)[0] ?? null;
};

/**
 * Returns `true` if the current component is being edited in context.
 */
export const selectIsEditingInContext = createSelector(
  (state: ComponentCanvasStateSlice) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  selectIsComponentCanvasActive,
  (focusedSymbolNativeIdPath, isComponentCanvasActive) =>
    isEditingComponentInContext({
      focusedSymbolNativeIdPath,
      isComponentCanvasActive,
    })
);

const selectComponents = (state: ComponentCanvasStateSlice) =>
  state.DesignerStore.components;

/**
 * Returns the variant associated with the active frame in the component canvas.
 * In the component canvas we always have a focused symbol path, since we are always editing a component, so
 * we can use that to find the active frame.
 */
export const selectActiveFrameStyleVariant = createSelector(
  selectComponents,
  (state: ComponentCanvasStateSlice) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  (components, focusedSymbolNativeIdPath) => {
    if (focusedSymbolNativeIdPath && focusedSymbolNativeIdPath.length > 0) {
      const activeFrame = getActiveFrameElement(
        components,
        focusedSymbolNativeIdPath
      );

      if (!activeFrame) return null;

      return getVariantAssociatedWithFrame(activeFrame.id, components);
    }
  }
);

/**
 * Returns the id of the Frame at the specified native id path
 */
export const getFrameIdAtPath = (
  components: Component.ComponentMap,
  nativeIdPath: Array<string>
) => {
  const activeFrame = getActiveFrameElement(components, nativeIdPath);
  return activeFrame ? activeFrame.id : null;
};

/**
 * Resolves which Component Canvas grid frame to select for `scenarioId`,
 * preserving the active style-variant track from the current frame selection
 * or focused path. Shared by ComponentCanvas / UiNode / External stores so
 * scenario-selection intent stays off the UI call site.
 */
export const resolveScenarioFrameElementId = (
  state: ComponentCanvasStateSlice,
  scenarioId: string
): string | null => {
  const currentSelectedFrameId =
    state.ComponentCanvasStore.selectedFrameElementId ??
    getFrameIdAtPath(
      state.DesignerStore.components,
      state.UiNodeStore.focusedSymbolNativeIdPath
    );
  return getScenarioFrameIdPreservingVariant(
    selectComponentCanvasComponentId(state),
    scenarioId,
    currentSelectedFrameId,
    state.DesignerStore.components
  );
};

export const selectIsComponentSelected = (state: ComponentCanvasStateSlice) =>
  state.UiNodeStore.focusedSymbolNativeIdPath.length > 0;

export const selectSelectedFrameElementId = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.selectedFrameElementId;

export const selectFrameOrientationSettings = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.frameOrientation;

/**
 * Returns the active frame's multi-state preview scenario id when the user
 * is focused on a `(variant × scenario)` grid cell, else `null`.
 *
 * Delegates the frame-id → scenario mapping to `getScenarioIdForGridFrame`,
 * which resolves against the same `getGridCells` enumeration the grid is
 * emitted from. Returns `null` for plain variant frames (components with no
 * stored states), mirroring how `selectActiveFrameStyleVariant` returns null
 * when not applicable. Used by the canvas breadcrumb to show
 * "<component> › Step 2" the same way variant breadcrumbs show
 * "<component> › Hover".
 */
export const selectActiveFrameScenarioId = createSelector(
  selectComponents,
  (state: ComponentCanvasStateSlice) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  (state: ComponentCanvasStateSlice) => selectComponentCanvasComponentId(state),
  (components, focusedSymbolNativeIdPath, componentId): string | null => {
    if (!componentId || !focusedSymbolNativeIdPath?.length) return null;
    const activeFrame = getActiveFrameElement(
      components,
      focusedSymbolNativeIdPath
    );
    if (!activeFrame) return null;
    return getScenarioIdForGridFrame(components, componentId, activeFrame.id);
  }
);

/**
 * After a preview scenario is removed, returns the id of the neighbouring
 * scenario whose grid frame should take over the active selection — but only
 * when the removed scenario was the currently active one. Returns `null` when
 * the removed scenario wasn't active (the selection is unaffected) or when no
 * scenarios remain (the grid collapses back to variant-only frames, so there
 * is nothing to reselect).
 *
 * Prefers the next row, falling back to the new last row when the tail was
 * removed. Computed from the pre-removal state, which is safe because grid
 * frame/instance ids are deterministic per `(componentId, variantId,
 * scenarioId)`: a surviving neighbour resolves to the same frame both before
 * and after the removal's grid re-sync.
 */
export function getScenarioIdToSelectAfterRemoval(
  state: ComponentCanvasStateSlice,
  componentId: string,
  removedScenarioId: string
): string | null {
  if (selectActiveFrameScenarioId(state) !== removedScenarioId) return null;

  const component = selectComponents(state).get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  const scenarios = component
    ? Component.getPreviewScenarios(component)
    : undefined;
  if (!scenarios || scenarios.length === 0) return null;

  const removedIndex = scenarios.findIndex(
    (scenario) => scenario.id === removedScenarioId
  );
  const remaining = scenarios.filter(
    (scenario) => scenario.id !== removedScenarioId
  );
  if (removedIndex === -1 || remaining.length === 0) return null;

  return remaining[Math.min(removedIndex, remaining.length - 1)]?.id ?? null;
}

/**
 * Returns `true` if the currently selected element is the root element of the
 * component instance hosted within the active frame.
 * This is used to determine if pressing Up should navigate to the frame selection.
 */
export const selectIsVariantRootElementSelected = createSelector(
  selectComponents,
  (state: ComponentCanvasStateSlice) =>
    state.UiNodeStore.focusedSymbolNativeIdPath,
  (state: ComponentCanvasStateSlice) => state.UiNodeStore.selectedNativeIdPath,
  (components, focusedSymbolNativeIdPath, selectedNativeIdPath) => {
    if (!focusedSymbolNativeIdPath || focusedSymbolNativeIdPath.length === 0) {
      return false;
    }

    if (!selectedNativeIdPath || selectedNativeIdPath.length === 0) {
      return false;
    }

    const activeFrame = getActiveFrameElement(
      components,
      focusedSymbolNativeIdPath
    );

    if (!activeFrame) return false;

    const rootElement = getVariantRootElementFromFrame(activeFrame, components);
    if (!rootElement) return false;

    // The selected element is the root if its element id matches the root element's id
    const targetRootId = getTargetElementId(selectedNativeIdPath);
    return targetRootId === rootElement.id;
  }
);

/**
 * Selector that returns the ordered array of global component IDs.
 */
export const selectGlobalComponentIds = createSelector(
  (state: DesignerStores) =>
    state.SiteDataStore.globalComponentSettings?.components,
  (globalComponents): string[] => {
    if (!Array.isArray(globalComponents)) return emptyArray;

    return globalComponents.length > 0
      ? globalComponents.map((entry) => entry.id)
      : emptyArray;
  }
);

/**
 * Returns the pending follow selection (element or frame) if one exists.
 * This is used when following another user to a component canvas to apply
 * their selection after the page loads.
 */
export const selectPendingFollowSelection = (
  state: ComponentCanvasStateSlice
) => state.ComponentCanvasStore.pendingFollowSelection;

/**
 * Returns the appropriate `GetBaseVariantId` strategy for the current
 * Component Canvas — `getBaseVariantIdForCodeComponent` when editing a code
 * component, `getBaseVariantIdForNativeComponent` otherwise.
 */
export const selectGetBaseVariantId = (state: {
  PageStore: PageState;
  DesignerStore: DesignerStateType;
  ComponentCanvasStore: ComponentCanvasState;
}): GetBaseVariantId => {
  // While entering a Component Canvas, currentPageId/componentPages still
  // reflect the previous page. Consult pendingCanvasComponentId first so the
  // resolver picks the right strategy during the transition window.
  const pendingId = state.ComponentCanvasStore?.pendingCanvasComponentId;
  if (pendingId) {
    const component = getLiveComponentMap(state.DesignerStore).get(
      qualify(Plugins.name('__SitePlugin'), Component.name(pendingId))
    );
    return component && Component.getSource(component)
      ? getBaseVariantIdForCodeComponent
      : getBaseVariantIdForNativeComponent;
  }

  return state.PageStore && selectIsCodeComponentCanvas(state)
    ? getBaseVariantIdForCodeComponent
    : getBaseVariantIdForNativeComponent;
};
